import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://floridahousesforsale.deals';

const buySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Buy a Florida Home | Step-by-Step Buyer\'s Guide',
  description: 'Learn the step-by-step process to buy a home in Florida with Amparo Jarquin. From pre-approval to getting the keys — expert guidance every step of the way.',
  url: `${SITE_URL}/buy`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Buy', item: `${SITE_URL}/buy` },
    ],
  },
};

const steps = [
  {
    number: '1',
    title: 'WHAT CAN YOU AFFORD?',
    description:
      'First, obtain a pre-approval document from a reliable, responsive lender who will be with you throughout the financing process. Your credit worthiness, income and assets will be analyzed. Be sure to provide all information promptly. Your agent can suggest proven, vetted lenders.',
  },
  {
    number: '2',
    title: 'BEGIN YOUR SEARCH',
    description:
      "Once you have narrowed down areas where you'd like to live, Amparo can assist you in identifying the home that best fits you and your family. You'll have access to our cutting-edge search function which features neighborhood and school statistics, market data and walk scores. Your agent will help refine your search and assist you in making an informed decision.",
  },
  {
    number: '3',
    title: 'HOW WILL YOU MAKE A WINNING OFFER?',
    description:
      "Your agent, a local market expert, will guide you in offering the most competitive price and terms using extensive market knowledge and various tools like sales trends, absorption rates, and escalation clauses. In today's market, bidding wars are common. Your agent knows all the right moves in this stressful, fast-moving game.",
  },
  {
    number: '4',
    title: 'CONDUCT HOME INSPECTION AND OBTAIN MORTGAGE APPROVAL AND INSURANCE',
    description:
      'Once your offer has been accepted, Amparo can connect you with skilled home inspectors, real estate attorneys, mortgage lenders, insurance agents and any other home-related vendors that might be needed. Your agent will manage every aspect of the transaction and will help you find resolutions to inevitable challenges.',
  },
  {
    number: '5',
    title: 'WHEN DO YOU GET THE KEYS?',
    description:
      "Your agent will arrange a final walk-through before closing to verify the current condition of the property. Don't skip this step. After you sign the closing documents and the deed is recorded, you will have the keys to your castle! Congratulations!",
  },
];

export default function BuyPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Buy a Home in Florida | Buyer's Guide | Amparo Jarquin REALTOR®"
        description="Ready to buy a home in Florida? Amparo Jarquin guides you from pre-approval to closing. Browse Florida listings and start your home buying journey today."
        keywords="buy home Florida, Florida home buying guide, Florida first time buyer, buy house Florida, Florida REALTOR buyer"
        canonical="/buy"
        schema={buySchema}
      />
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-72 md:h-96 flex items-center justify-center overflow-hidden mt-0">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ffb6a826430582b60abf76a75246350b.jpg"
            alt="Buy a Florida home"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/55"></div>
        </div>
        <div className="relative z-10 text-center px-6 w-full">
          <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase mb-3">
            Amparo Jarquin Real Estate
          </p>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
            Buy Your <span className="text-amber-400">Florida Home</span>
          </h1>
          <p className="text-white/75 text-base md:text-lg max-w-xl mx-auto mt-4">
            From pre-approval to getting the keys — Amparo guides you every step of the way.
          </p>
        </div>
      </section>

      {/* Buying Process Steps */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-3">The Buying Process</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">How We Find Your Home</h2>
            <div className="w-12 h-1 bg-amber-500 mx-auto mt-6"></div>
          </div>

          <div className="space-y-20">
            {steps.map((step) => (
              <div
                key={step.number}
                className="flex flex-col md:flex-row items-start gap-8 group"
              >
                {/* Big Number */}
                <div className="flex-shrink-0">
                  <span
                    className="text-[120px] md:text-[160px] font-bold leading-none select-none transition-colors duration-300 group-hover:text-amber-500"
                    style={{
                      color: 'transparent',
                      WebkitTextStroke: '2px #1c3a5e',
                    }}
                  >
                    {step.number}
                  </span>
                </div>
                {/* Content */}
                <div className="pt-4 md:pt-8">
                  <h3 className="text-base font-bold text-gray-900 tracking-wide mb-4">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ready to Find Your Dream Home?</h2>
          <p className="text-gray-600 text-base mb-8 leading-relaxed">
            Let Amparo help you navigate the Florida market and find the perfect home for you and your family.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/listings"
              className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm tracking-wide"
            >
              Browse Listings
            </Link>
            <Link
              to="/request"
              className="px-8 py-4 border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm tracking-wide"
            >
              Request Information
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
